import React, { useEffect, useState } from "react";
import { useSpecialism } from "../contexts/SpecialismContext";
import { collection, getDocs, addDoc, deleteDoc, query, where } from "firebase/firestore";
import { db } from "../firebase/config";
import { Link } from "react-router-dom";
import { getAuth } from "firebase/auth";

const Flashcards: React.FC = () => {
  const { specialismCode } = useSpecialism();
  const [decks, setDecks] = useState<string[]>([]);
  const [debug, setDebug] = useState<string[]>([]);
  const [newDeckName, setNewDeckName] = useState("");
  const [creating, setCreating] = useState(false);
  const [showDeleteDeckModal, setShowDeleteDeckModal] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const auth = getAuth();
  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!specialismCode) {
      setDebug(["No specialismCode"]);
      return;
    }
    const fetchDecks = async () => {
      const colName = `flashcards_${specialismCode.toLowerCase()}`;
      setDebug([`Collection: ${colName}`]);
      try {
        const snapshot = await getDocs(collection(db, colName));
        setDebug(prev => [...prev, `Docs found: ${snapshot.size}`]);
        const deckSet = new Set<string>();
        snapshot.forEach(doc => {
          const data = doc.data();
          setDebug(prev => [...prev, `Doc: ${doc.id}, Data: ${JSON.stringify(data)}`]);
          if (data.deck) {
            deckSet.add(data.deck);
          }
        });
        setDecks(Array.from(deckSet));
        setDebug(prev => [...prev, `Unique decks: ${JSON.stringify(Array.from(deckSet))}`]);
      } catch (err) {
        setDebug(prev => [...prev, `Error: ${err}`]);
      }
    };
    fetchDecks();
  }, [specialismCode]);

  const handleCreateDeck = async () => {
    if (!specialismCode || !newDeckName.trim()) return;
    setCreating(true);
    const colName = `flashcards_${specialismCode.toLowerCase()}`;
    try {
      // Create a placeholder document to establish the deck
      const newDoc = {
        deck: newDeckName.trim(),
        frontText: "",
        backText: "",
        frontImageURL: [],
        backImageURL: [],
        createdAt: { seconds: Math.floor(Date.now() / 1000) },
        createdBy: currentUser ? currentUser.uid : "unknown",
        isPlaceholder: true, // Mark as placeholder so it can be edited/deleted easily
      };
      await addDoc(collection(db, colName), newDoc);
      // Add to local state
      setDecks([...decks, newDeckName.trim()]);
      setNewDeckName("");
      setDebug(prev => [...prev, `Created new deck: ${newDeckName.trim()}`]);
    } catch (err) {
      setDebug(prev => [...prev, `Create deck error: ${err}`]);
    }
    setCreating(false);
  };

  const handleDeleteDeck = async (deckName: string) => {
    if (!specialismCode) return;
    setDeleting(true);
    const colName = `flashcards_${specialismCode.toLowerCase()}`;
    try {
      // Get all documents in this deck
      const snapshot = await getDocs(collection(db, colName));
      const deletePromises: Promise<void>[] = [];
      
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.deck === deckName) {
          deletePromises.push(deleteDoc(doc.ref));
        }
      });
      
      // Delete all documents in the deck
      await Promise.all(deletePromises);
      
      // Remove from local state
      setDecks(decks.filter(deck => deck !== deckName));
      setShowDeleteDeckModal(null);
      setDebug(prev => [...prev, `Deleted deck: ${deckName}`]);
    } catch (err) {
      setDebug(prev => [...prev, `Delete deck error: ${err}`]);
    }
    setDeleting(false);
  };

  return (
    <>
      <p>This is a test text paragraph for Flashcards.</p>
      <h3>Available Decks:</h3>
      <ul>
        {decks.map(deck => (
          <li key={deck} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <span>{deck}</span>
            <Link
              to={`/cards/${encodeURIComponent(deck)}`}
              style={{
                background: "none",
                border: "0px solid #1976d2",
                color: "#1976d2",
                cursor: "pointer",
                fontSize: "14px",
                padding: "4px 6px",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "24px",
                height: "24px",
                textDecoration: "none",
              }}
              title="Edit deck"
            >
              ✏️
            </Link>
            <button
              onClick={() => setShowDeleteDeckModal(deck)}
              style={{
                background: "none",
                border: "none",
                color: "#e53935",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "bold",
                padding: "2px 6px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "24px",
                height: "24px",
              }}
              title="Delete deck"
            >
              ×
            </button>
          </li>
        ))}
        <li style={{ marginTop: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
          <input
            type="text"
            value={newDeckName}
            onChange={e => setNewDeckName(e.target.value)}
            placeholder="Enter new deck name"
            style={{
              padding: "8px 12px",
              border: "1px solid #ccc",
              borderRadius: 4,
              fontSize: "14px",
              minWidth: 200,
            }}
            onKeyDown={e => {
              if (e.key === "Enter" && newDeckName.trim()) {
                handleCreateDeck();
              }
            }}
            disabled={creating}
          />
          <button
            onClick={handleCreateDeck}
            disabled={creating || !newDeckName.trim()}
            style={{
              background: "#1976d2",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              padding: "8px 16px",
              cursor: creating || !newDeckName.trim() ? "not-allowed" : "pointer",
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            {creating ? "Creating..." : "Create Deck"}
          </button>
        </li>
      </ul>

      {/* Delete deck confirmation modal */}
      {showDeleteDeckModal && (
        <div
          style={{
            position: "fixed",
            zIndex: 1100,
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowDeleteDeckModal(null)}
        >
          <div
            style={{
              background: "#fff",
              padding: "32px",
              borderRadius: 8,
              boxShadow: "0 0 24px #000",
              minWidth: 360,
              textAlign: "center",
            }}
            onClick={e => e.stopPropagation()}
          >
            <p style={{ marginBottom: 24 }}>
              Are you sure you want to delete the deck "{showDeleteDeckModal}"? This will permanently delete all flashcards in this deck.
            </p>
            <button
              style={{
                background: "#e53935",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                padding: "8px 24px",
                marginRight: 16,
                cursor: deleting ? "not-allowed" : "pointer",
                fontWeight: "bold",
              }}
              onClick={() => handleDeleteDeck(showDeleteDeckModal)}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete Deck"}
            </button>
            <button
              style={{
                background: "#1976d2",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                padding: "8px 24px",
                cursor: "pointer",
                fontWeight: "bold",
              }}
              onClick={() => setShowDeleteDeckModal(null)}
              disabled={deleting}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <h4>Debug Output:</h4>
      <pre style={{ background: "#f5f5f5", padding: "8px", fontSize: "0.9em" }}>
        {debug.join("\n")}
      </pre>
    </>
  );
};

export default Flashcards;